import { makeVar, useReactiveVar } from '@apollo/client'
import { createBatchedReactiveVar } from '@src/utils/batchedReactiveVar'
import { useContext } from 'react'

import { BasketOutletContext } from '../outletFulfilmentAndBasketHooks/useOutletFulfilment/context/BasketOutletProvider'
import { CurrentOutletContext } from '../outletFulfilmentAndBasketHooks/useOutletFulfilment/context/CurrentOutletProvider'

enum BottomSheetStackLocation {
  BASKET = 'BASKET',
  CURRENT_OUTLET = 'CURRENT_OUTLET',
  OUTSIDE_CONTEXT = 'OUTSIDE_CONTEXT',
}

// These are all the types of content that can be displayed in the BottomSheet.
// Used to track which BottomSheet instance this hook's properties and methods refer to.
enum InnerContentType {
  SERVICE_COMPONENT = 'SERVICE_COMPONENT',
  POSTCODE = 'POSTCODE',
  DAY_PICKER = 'DAY_PICKER',
  TIME_PICKER = 'TIME_PICKER',
  ADDRESS_PICKER = 'ADDRESS_PICKER',
  PLACE_SEARCH = 'PLACE_SEARCH',
  BUSINESS_SEGMENT_CATEGORIES = 'BUSINESS_SEGMENT_CATEGORIES',
  OUTLET_PREVIEW_PICKER = 'OUTLET_PREVIEW_PICKER',
  DELIVERY_ZONE = 'DELIVERY_ZONE',
}

export type BottomSheetController = {
  type: InnerContentType
  isOpen: boolean
  isMinimised: boolean
  open: () => void
  close: () => void
  stack: BottomSheetStackData | null
}

type ControllersByInnerContentType = {
  serviceComponentBottomSheetController: BottomSheetController
  postcodeBottomSheetController: BottomSheetController
  dayPickerBottomSheetController: BottomSheetController
  timePickerBottomSheetController: BottomSheetController
  addressPickerBottomSheetController: BottomSheetController
  placeSearchBottomSheetController: BottomSheetController
  categoriesBottomSheetController: BottomSheetController
  outletPreviewPickerBottomSheetController: BottomSheetController
  deliveryZoneBottomSheetController: BottomSheetController
}

/**
 * Manages the state of a specific instance of BottomSheet
 * within awareness of other open BottomSheet instances (differentiated by InnerContentType)
 * This allows multiple BottomSheets to be open at the same time, and all but the most recently opened will be minimised
 * ie The sheet is still "open" so as not to lose state, but visually it's hidden and is not fighting for focus with other BottomSheets
 */
export const useBottomSheetStack = (): ControllersByInnerContentType => {
  const bottomSheetStackData = useReactiveVar(bottomSheetStackReactiveVar)
  const isBasketContext = Boolean(useContext(BasketOutletContext))
  const isCurrentOutletContext = Boolean(useContext(CurrentOutletContext))

  const getStackLocation = () => {
    if (isBasketContext) {
      return BottomSheetStackLocation.BASKET
    }
    if (isCurrentOutletContext) {
      return BottomSheetStackLocation.CURRENT_OUTLET
    }
    return BottomSheetStackLocation.OUTSIDE_CONTEXT
  }
  const currentStackLocation = getStackLocation()

  const createController = (innerContentType: InnerContentType) => {
    // To be open, the BottomSheet must be in the stack
    // and the BottomSheet stack must have been opened in the same context that this hook is currently being called
    // This prevents basket AND outlet page instances of the BottomSheet with the same InnerContentType from opening at the same time
    const isOpen =
      (bottomSheetStackData?.stack.has(innerContentType) &&
        currentStackLocation === bottomSheetStackData?.location) ||
      false

    // To be minimised the BottomSheet must be open (in the correct context, see above)
    // and not be the most recently opened BottomSheet
    const stack = Array.from(bottomSheetStackData?.stack || [])
    const isMinimised =
      isOpen && stack.indexOf(innerContentType) < stack.length - 1

    const open = () =>
      batchedBottomSheetStack.update(current => {
        // get and clone existing stack
        const existingStack = current?.stack ?? new Set<InnerContentType>()
        const nextStack = new Set(existingStack)

        // add inner content type to the stack
        nextStack.add(innerContentType)

        // return updated stack
        return {
          location: currentStackLocation,
          stack: nextStack,
        }
      })

    const close = () =>
      batchedBottomSheetStack.update(current => {
        // get and clone existing stack
        const existingStack = current?.stack ?? new Set<InnerContentType>()
        const nextStack = new Set(existingStack)

        // remove inner content type from the stack
        nextStack.delete(innerContentType)

        // return updated stack
        return {
          location: currentStackLocation,
          stack: nextStack,
        }
      })
    return {
      type: innerContentType,
      isOpen,
      isMinimised,
      open,
      close,
      stack: bottomSheetStackData,
    }
  }

  return {
    serviceComponentBottomSheetController: createController(
      InnerContentType.SERVICE_COMPONENT
    ),
    postcodeBottomSheetController: createController(InnerContentType.POSTCODE),
    dayPickerBottomSheetController: createController(
      InnerContentType.DAY_PICKER
    ),
    timePickerBottomSheetController: createController(
      InnerContentType.TIME_PICKER
    ),
    addressPickerBottomSheetController: createController(
      InnerContentType.ADDRESS_PICKER
    ),
    placeSearchBottomSheetController: createController(
      InnerContentType.PLACE_SEARCH
    ),
    categoriesBottomSheetController: createController(
      InnerContentType.BUSINESS_SEGMENT_CATEGORIES
    ),
    outletPreviewPickerBottomSheetController: createController(
      InnerContentType.OUTLET_PREVIEW_PICKER
    ),
    deliveryZoneBottomSheetController: createController(
      InnerContentType.DELIVERY_ZONE
    ),
  }
}

// In the reactive state we store the location of the currently open stack of BottomSheets.
// And the stack itself: a list of InnerContentTypes which tracks the open BottomSheets in the order they were opened.
type BottomSheetStackData = {
  location: BottomSheetStackLocation
  stack: Set<InnerContentType>
}
export const bottomSheetStackReactiveVar = makeVar<BottomSheetStackData | null>(
  null
)

// Create a batched wrapper to prevent write-conflicts
const batchedBottomSheetStack = createBatchedReactiveVar(
  bottomSheetStackReactiveVar
)
